/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { SwapOutlined, TagOutlined } from '@ant-design/icons'
import React from 'react'
import Text from '../../../../components/ts-components/Text'
import { formatCurrency } from '@sportninja/common/utils/utils'
import {
  teamColumn,
  detailsColumnWithTeam,
  cardContainer,
  cardContent,
  infoRow,
  detailsColumn,
  costColumn,
  costDetails,
  divider,
  actionRow,
  changeDivisionButton,
  promoCodeButton,
} from './styles'
import { resolveCardDeposit } from './utils'

/**
 * Division information interface
 */
interface Division {
  name: string
  division_age?: { name: string }
  division_gender?: { name: string }
  team_cost?: number
  player_cost?: string
  deposit?: number
  player_deposit?: number | null
  division_age_v2?: string | null
  /** Team Budget: the fee is split among the players after registration. */
  cost_distribution_enabled?: boolean | null
  /** Team Budget: the deposit — the Minimum To Qualify, pre-tax. */
  cost_distribution_threshold?: number | string | null
}

/**
 * Coupon information interface
 */
interface CouponInfo {
  id: string
  code: string
  discount: number
  type: 'percentage' | 'amount'
  subtotal: number
  discount_subtotal?: number
  discount_amount?: number
  deposit_subtotal?: number
  deposit_discount_subtotal?: number
  deposit_discount_amount?: number
  applies_to_deposit?: boolean
}

/**
 * Props interface for the SelectedDivisionCard component
 */
interface SelectedDivisionCardProps {
  division: Division
  couponInfo?: CouponInfo | null
  isTeamRep: boolean
  colors: typeof import('@sportninja/common/constants/appColors').default
  currency?: { currency_code?: string }
  onChangeDivision?: () => void
  /**
   * Rendered as a column between the division details and the cost — the team
   * this registration is for, when there is one.
   *
   * A slot rather than team props, so this card keeps knowing only about
   * divisions and costs; the caller owns what a team looks like.
   */
  teamSlot?: React.ReactNode
  onTogglePromoCode?: () => void
  showPromoCode?: boolean
  isCanlan?: boolean
}

const strikethroughStyle = 'text-decoration: line-through; margin-right: 4px;'

/**
 * SelectedDivisionCard displays the selected division information including
 * division details, costs, coupon information, and action buttons.
 *
 * This is a purely presentational component that receives all data as props
 * and delegates business logic to parent components.
 */
export const SelectedDivisionCard: React.FC<SelectedDivisionCardProps> = ({
  division,
  couponInfo,
  isTeamRep,
  colors,
  currency,
  onChangeDivision,
  teamSlot,
  onTogglePromoCode,
  showPromoCode = false,
  isCanlan = false,
}) => {
  // Sep 24 (R6) — a Team Budget rep's deposit is the Minimum To Qualify, and no
  // coupon strikes a Team Budget figure through (resolveCardDeposit).
  const { deposit, couponApplies: hasCoupon } = resolveCardDeposit({
    division,
    isTeamRep,
    hasCoupon: !!(couponInfo?.id && couponInfo.id !== ''),
  })
  const hasDepositDiscount = hasCoupon && !!couponInfo?.applies_to_deposit
  const isTeamBudgetRep =
    isTeamRep && Boolean(division.cost_distribution_enabled)

  // Calculate the cost based on registration type
  const baseCost = isTeamRep
    ? division.team_cost
    : typeof division.player_cost === 'string'
    ? parseFloat(division.player_cost) || 0
    : division.player_cost

  const fmt = (amount: number) =>
    formatCurrency(amount, currency?.currency_code?.toUpperCase())

  return (
    <div css={cardContainer}>
      {/* The heading sits above the card only when nothing else shares it. With
          a team column beside the division, each side labels itself inline
          instead — one heading over two subjects would name only half. */}
      {teamSlot ? null : (
        <Text
          variant='captionLarge'
          weight='semiBold'
          color={colors.NEUTRAL_0}
          style='align-self: flex-start;'
        >
          Selected Division
        </Text>
      )}

      <div css={cardContent}>
        {/* Main information row */}
        <div css={infoRow}>
          {/* Division details column */}
          <div css={[detailsColumn, teamSlot && detailsColumnWithTeam]}>
            {teamSlot ? (
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.MAGNESIUM}
              >
                Selected Division
              </Text>
            ) : null}
            <Text
              variant='heading5'
              color={colors.WHITE}
              weight='semiBold'
              style='align-self: stretch;'
            >
              {division.name}
            </Text>
            <Text
              variant='paragraphSmall'
              color={colors.MAGNESIUM}
              weight='regular'
            >
              Age: {division.division_age_v2 || 'N/A'}
            </Text>
            <Text
              variant='paragraphSmall'
              color={colors.MAGNESIUM}
              weight='regular'
            >
              Gender: {division.division_gender?.name || 'N/A'}
            </Text>
            {/* A Team Budget rep's figures take no coupon (resolveCardDeposit). */}
            {couponInfo && couponInfo.discount && !isTeamBudgetRep ? (
              <Text
                variant='paragraphMedium'
                color={colors.SUCCESS_100}
                weight='semiBold'
              >
                {couponInfo.type === 'percentage'
                  ? `Coupon Code: ${couponInfo.code} - ${couponInfo.discount}% off`
                  : `Coupon Code: ${couponInfo.code} - ${fmt(
                      couponInfo.discount
                    )} off`}
              </Text>
            ) : null}
          </div>

          {teamSlot ? <div css={teamColumn}>{teamSlot}</div> : null}

          {/* Cost information column */}
          <div css={costColumn}>
            {/* Deposit row (above cost) */}
            {deposit ? (
              <div css={costDetails}>
                <Text
                  variant='paragraphSmall'
                  color={colors.MAGNESIUM}
                  weight='regular'
                >
                  Deposit
                </Text>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 4px;
                  `}
                >
                  {hasDepositDiscount && (
                    <Text
                      variant='heading6'
                      color={colors.PRIMARY}
                      weight='semiBold'
                      style={strikethroughStyle}
                    >
                      {fmt(couponInfo?.deposit_subtotal ?? deposit ?? 0)}
                    </Text>
                  )}
                  <Text
                    variant='heading6'
                    color={
                      hasDepositDiscount ? colors.SUCCESS_100 : colors.PRIMARY
                    }
                    weight='semiBold'
                  >
                    {hasDepositDiscount
                      ? fmt(couponInfo?.deposit_discount_subtotal ?? 0)
                      : fmt(deposit ?? 0)}
                  </Text>
                </div>
              </div>
            ) : null}

            {/* Cost row */}
            <div css={costDetails}>
              <Text
                variant='paragraphSmall'
                color={colors.MAGNESIUM}
                weight='regular'
              >
                Cost Per {isTeamRep ? 'Team' : 'Player'}
              </Text>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 4px;
                `}
              >
                {hasCoupon && (
                  <Text
                    variant='heading6'
                    color={colors.NEUTRAL_0}
                    weight='semiBold'
                    style={strikethroughStyle}
                  >
                    {fmt(couponInfo?.subtotal ?? baseCost ?? 0)}
                  </Text>
                )}
                <Text
                  variant='heading6'
                  color={hasCoupon ? colors.SUCCESS_100 : colors.NEUTRAL_0}
                  weight='semiBold'
                >
                  {hasCoupon
                    ? fmt(couponInfo?.discount_subtotal ?? 0)
                    : fmt(baseCost ?? 0)}
                </Text>
              </div>
            </div>

            {/* The amounts above are base costs — tax and the League/Payment
                Processing Fees are only calculated at checkout. Margin-top
                only: the column is space-between, so the note must stay
                attached to the cost above rather than drift to the bottom. */}
            <div
              css={css`
                ${costDetails}
                margin-top: 4px;
              `}
            >
              <Text
                variant='paragraphXSmall'
                color={colors.MAGNESIUM}
                weight='regular'
              >
                * Additional taxes and fees apply at checkout.
              </Text>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div css={divider} />

        {/* Action buttons row */}
        <div css={actionRow}>
          <p onClick={onChangeDivision} css={changeDivisionButton()}>
            <SwapOutlined style={{ fontSize: 16 }} />
            {/* One action for the whole card. It returns to the start step,
                where the division AND the team are chosen, so naming either
                one would understate where it goes. */}
            Go Back To Change Division/Team
          </p>

          <p
            onClick={onTogglePromoCode}
            css={promoCodeButton(showPromoCode, isCanlan)}
          >
            <TagOutlined style={{ fontSize: 16 }} />
            Coupon Code
          </p>
        </div>
      </div>
    </div>
  )
}

export default SelectedDivisionCard
